import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { SalvButton, SalvDropdown, SalvFieldHeader, SalvTextArea, Size } from '@salvapp/ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons/faPenToSquare';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { faXmarkLarge } from '@fortawesome/pro-solid-svg-icons';
import { ResponseError } from '../api-internal';

interface FieldEditorFormValues {
  value: string;
}

interface FieldViewProps {
  label: ReactNode | string;
  value: any;
  onEdit?: () => void;
  description?: string;
  disabled?: boolean;
  size?: Size;
}
interface FieldEditorProps {
  label: ReactNode | string;
  value: any;
  onConfirm: (newValue: any) => void | Promise<any>;
  required?: boolean;
  options?: DropdownOption[];
  fieldWidth?: number;
  isUpper?: boolean;
  description?: string;
  disabled?: boolean;
  size?: Size;
}

// TODO: deprecated, use ./common/form/FieldView and FieldEditor instead
export const FieldView = ({ label, value, onEdit, description, disabled, size = Size.SMALL }: FieldViewProps) => (
  <div>
    <div className='d-flex align-items-center'>
      <SalvFieldHeader
        className='m-b-4'
        size={size}
        label={
          <div>
            {label}
            {!disabled && onEdit && (
              <FontAwesomeIcon
                icon={faPenToSquare}
                onClick={onEdit}
                className='text-secondary cursor-pointer m-l-8'
                aria-label='edit field icon'
              />
            )}
          </div>
        }
        description={description}
      />
    </div>

    <div>{value || '-'}</div>
  </div>
);

const FieldEditor = ({
  label,
  value,
  onConfirm,
  required = true,
  options,
  fieldWidth,
  isUpper,
  description,
  disabled = false,
  size,
}: FieldEditorProps) => {
  const [isEditing, setEditing] = useState(false);

  const EditorView = () => {
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const initialValues = {
      value,
    };

    useEffect(() => {
      if (inputRef.current) {
        const input = inputRef.current;
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length); // Set cursor at the end
      }
    }, [inputRef]);

    const confirm = async (
      values: FieldEditorFormValues,
      { setSubmitting, setFieldError }: FormikHelpers<FieldEditorFormValues>
    ) => {
      setSubmitting(true);
      try {
        await onConfirm(values.value);
        setSubmitting(false);
        setEditing(false);
      } catch (e: any) {
        if (e instanceof ResponseError && e.response.status === 400) {
          setFieldError('value', (await e.response.json()).message);
        }
      }
    };

    return (
      <div>
        <div className='d-flex align-items-center'>
          <Formik initialValues={initialValues} onSubmit={confirm}>
            {({ isSubmitting, values, errors, setFieldValue }: FormikProps<FieldEditorFormValues>) => {
              return (
                <Form>
                  <SalvFieldHeader label={label} description={description} className='m-b-4' />
                  <div className='d-flex'>
                    {!options ? (
                      <div style={{ width: fieldWidth }}>
                        <SalvTextArea
                          ref={inputRef}
                          size={Size.SMALL}
                          value={values.value}
                          onChange={(value: string) => {
                            setFieldValue('value', isUpper ? value.toUpperCase() : value);
                          }}
                          required={required}
                          isInvalid={!!errors.value}
                          autoSizing={true}
                          minRows={1}
                          maxRows={1}
                        />
                        {errors.value && <div className='danger'>{errors.value}</div>}
                      </div>
                    ) : (
                      <SalvDropdown
                        options={options}
                        size={Size.SMALL}
                        onChange={(option) => {
                          if (option[0]) {
                            setFieldValue('value', option[0].value);
                          }
                        }}
                        value={options.filter((o) => o.value === values.value)}
                      />
                    )}
                    <SalvButton
                      type='submit'
                      variant='secondary'
                      disabled={isSubmitting}
                      size={Size.SMALL}
                      label='Confirm'
                      className='m-l-8'
                    />
                    <SalvButton
                      icon={faXmarkLarge}
                      variant='muted'
                      size={Size.SMALL}
                      onClick={() => setEditing(false)}
                      className='m-l-8'
                    />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    );
  };

  return isEditing ? (
    <EditorView />
  ) : (
    <FieldView
      label={label}
      description={description}
      value={options ? options.filter((o) => o.value === value)[0]?.label : value}
      onEdit={() => setEditing(true)}
      disabled={disabled}
      size={size}
    />
  );
};

export default FieldEditor;
